/**
 * ⌘K / Ctrl-K. Pages, recent leads, and the actions a rep takes all day.
 *
 * Keyboard-first is a Global Constraint and this is its front door: everything reachable here
 * is reachable without a mouse, and the palette itself never traps focus anywhere it cannot be
 * escaped from (Radix Dialog, via cmdk, handles that).
 *
 * Filtering is cmdk's fuzzy scorer over `value` + `keywords`, so "hand" finds Handoffs and
 * "अनीता" finds अनीता शर्मा — the Devanagari names have to be findable by the people who type them.
 *
 * Author: Danish Khan
 */
import { Command } from 'cmdk';
import type { ReactNode } from 'react';

export interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  /** Extra search terms — a lead's city, a page's old name, the shortcut letter. */
  keywords?: string[];
  shortcut?: string;
  run: () => void;
}

export interface CommandGroup {
  heading: string;
  items: CommandItem[];
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: CommandGroup[];
}

export function CommandPalette({ open, onOpenChange, groups }: CommandPaletteProps): ReactNode {
  const usable = groups.filter((g) => g.items.length > 0);

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command palette"
      shouldFilter
      className="fixed top-[12vh] left-1/2 z-[70] w-[min(36rem,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-[var(--h1-radius)] border border-line bg-surface shadow-2xl"
      overlayClassName="fixed inset-0 z-[65] bg-[var(--h1-overlay)]"
      contentClassName="outline-none"
    >
      <Command.Input
        autoFocus
        placeholder="Search pages, leads and actions…"
        aria-label="Search pages, leads and actions"
        className="w-full border-b border-line bg-transparent px-4 py-3.5 text-sm text-fg outline-none placeholder:text-fg-subtle"
      />
      <Command.List className="h1-scroll max-h-[min(24rem,60vh)] overflow-y-auto p-1.5">
        <Command.Empty className="px-3 py-6 text-center text-sm text-fg-muted">
          Nothing matches that.
        </Command.Empty>
        {usable.map((group) => (
          <Command.Group
            key={group.heading}
            heading={group.heading}
            className="px-1 pt-2 pb-1 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase [&_[cmdk-group-items]]:mt-1"
          >
            {group.items.map((item) => (
              <Command.Item
                key={item.id}
                value={`${item.label} ${(item.keywords ?? []).join(' ')}`}
                onSelect={() => {
                  onOpenChange(false);
                  item.run();
                }}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-sm text-fg data-[selected=true]:bg-brand-soft data-[selected=true]:text-brand-soft-fg"
              >
                <span className="min-w-0 flex-1 truncate normal-case">{item.label}</span>
                {item.hint && <span className="truncate text-[11px] normal-case text-fg-subtle">{item.hint}</span>}
                {item.shortcut && (
                  <kbd className="rounded border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-fg-muted">
                    {item.shortcut}
                  </kbd>
                )}
              </Command.Item>
            ))}
          </Command.Group>
        ))}
      </Command.List>
    </Command.Dialog>
  );
}
